import type {
  BlockSource,
  DataBinding,
  InteractionBinding,
} from './block.ts';
import type { JsonObject } from './json.ts';
import { z } from 'zod';
import {
  blockSourceSchema,
  dataBindingSchema,
  interactionBindingSchema,
} from './block.ts';
import { componentKeySchema } from './component.ts';
import { jsonObjectSchema } from './json.ts';

export interface PageCompositionChildBlock {
  id: string;
  componentKey: string;
  title: string;
  config: JsonObject;
  bindings: DataBinding[];
  interactions: InteractionBinding[];
  source: BlockSource;
  children?: PageCompositionChildBlock[];
  childRatios?: number[];
}

export interface PageCompositionChildBlockInput {
  id: string;
  componentKey: string;
  title?: string;
  config?: JsonObject;
  bindings?: z.input<typeof dataBindingSchema>[];
  interactions?: z.input<typeof interactionBindingSchema>[];
  source?: z.input<typeof blockSourceSchema>;
  children?: PageCompositionChildBlockInput[];
  childRatios?: number[];
}

/**
 * Agent 槽位 children 只描述组合意图，不允许携带 placement。
 * SplitView 等容器的子块最终 placement 由可信前端补默认值。
 */
export const pageCompositionChildBlockSchema: z.ZodType<
  PageCompositionChildBlock,
  z.ZodTypeDef,
  PageCompositionChildBlockInput
> = z.object({
  id: z.string().min(1).max(200),
  componentKey: componentKeySchema,
  title: z.string().max(100000).default(''),
  config: jsonObjectSchema.default({}),
  bindings: z.array(dataBindingSchema).max(100).default([]),
  interactions: z.array(interactionBindingSchema).max(100).default([]),
  source: blockSourceSchema.default({ type: 'catalog' }),
  children: z.array(z.lazy(() => pageCompositionChildBlockSchema)).max(100).optional(),
  childRatios: z.array(z.number().min(0).max(1)).max(100).optional(),
}).strict().superRefine((block, context) => {
  const inputs = new Set<string>();
  for (const [index, binding] of block.bindings.entries()) {
    if (inputs.has(binding.input)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['bindings', index, 'input'],
        message: `同一输入不能重复绑定：${binding.input}`,
      });
    }
    inputs.add(binding.input);
  }
});

/**
 * Agent 对模板槽位的内容指派。这里只描述组合意图；最终 placement
 * 始终由可信前端布局内核计算。
 */
export const pageCompositionAssignmentSchema = z.object({
  slotId: z.string().min(1).max(200),
  blockId: z.string().min(1).max(200).optional(),
  componentKey: componentKeySchema,
  title: z.string().max(100000).optional(),
  config: jsonObjectSchema.default({}),
  bindings: z.array(dataBindingSchema).max(100).default([]),
  interactions: z.array(interactionBindingSchema).max(100).default([]),
  source: blockSourceSchema.default({ type: 'catalog' }),
  children: z.array(pageCompositionChildBlockSchema).max(100).optional(),
  childRatios: z.array(z.number().min(0).max(1)).max(100).optional(),
}).strict();

export const pageComposeCommandSchema = z.object({
  type: z.literal('page.compose'),
  pageId: z.string().min(1).max(200),
  pageTitle: z.string().max(100000).optional(),
  templateId: z.string().min(1).max(200),
  params: jsonObjectSchema.default({}),
  assignments: z.array(pageCompositionAssignmentSchema).max(200).default([]),
  replaceExisting: z.boolean().default(false),
}).strict();

export type PageCompositionAssignment = z.infer<
  typeof pageCompositionAssignmentSchema
>;
export type PageComposeCommand = z.infer<typeof pageComposeCommandSchema>;
